import { IconType } from "react-icons";
import {
  LuAlertCircle,
  LuAlertTriangle,
  LuCheckCircle2,
  LuInfo,
} from "react-icons/lu";

import {
  AdvisorBlock,
  AdvisorSentiment,
  IAdvisorCalloutBlock,
  IAdvisorComparisonTableBlock,
  IAdvisorPackageCardBlock,
  IAdvisorStatRowBlock,
  IAdvisorTextBlock,
} from "@types";

import { cn } from "../cn";
import { isChartableDistribution } from "./chartCommon";
import { DistributionTable } from "./DistributionTable";
import { MarketPercentileCards } from "./MarketPercentileCards";
import {
  BonusBreakdownTable,
  PackageCompositionCard,
  S4ComparisonsTable,
} from "./RecommendationBlocks";
import { CALLOUT_STYLES, ROW_STYLES } from "./sentiment";
import { numericColumns } from "./tableUtils";

/**
 * Renders the advisor's five block types. This is the whole rendering surface of the API.
 * Every value below is printed exactly as the backend sent it — no formatting, rounding,
 * currency handling, or reordering happens here.
 */

// The S1B_INTERNAL peer-median waterfall is hidden by default (mirrors the reference app's own
// FEATURES.showPeerWaterfall flag). Flip to true to bring it back.
const SHOW_PEER_WATERFALL = false;

/** The backend emits a little Markdown-ish emphasis (**bold**) and real newlines. */
export function RichText({ content }: { content: string }) {
  return (
    <>
      {content.split("\n\n").map((paragraph, i) => (
        <p key={i} className={cn("leading-relaxed", i > 0 && "mt-3")}>
          {paragraph.split(/(\*\*[^*]+\*\*)/g).map((part, j) =>
            part.startsWith("**") && part.endsWith("**") ? (
              <strong key={j} className="font-semibold">
                {part.slice(2, -2)}
              </strong>
            ) : (
              <span key={j}>{part}</span>
            )
          )}
        </p>
      ))}
    </>
  );
}

function Text({ block }: { block: IAdvisorTextBlock }) {
  return (
    <div className="text-sm text-gray-800">
      <RichText content={block.content} />
    </div>
  );
}

const CALLOUT_ICON: Record<AdvisorSentiment, IconType> = {
  positive: LuCheckCircle2,
  neutral: LuInfo,
  warning: LuAlertTriangle,
  critical: LuAlertCircle,
  info: LuInfo,
};

function Callout({ block }: { block: IAdvisorCalloutBlock }) {
  const style = CALLOUT_STYLES[block.sentiment];
  const Icon = CALLOUT_ICON[block.sentiment];
  return (
    // Supporting note, not a banner: a coloured left rule + icon over neutral text, no fill.
    // items-start (not the flex default of stretch) keeps the icon a fixed size regardless of
    // how many lines the text wraps to — without it, the icon's cross-axis stretched to match
    // the text block's height, so it visibly shrank/grew with the amount of text next to it.
    // The inline width/height is a belt-and-suspenders fixed pixel size on top of that, since
    // react-icons renders a default 1em-relative size via an SVG attribute alongside the h-4/w-4
    // classes.
    <div
      className={cn(
        "flex items-start gap-2.5 border-l-2 py-0.5 pl-3",
        style.accent
      )}
    >
      <Icon
        className={cn("mt-0.5 h-4 w-4 shrink-0", style.icon)}
        style={{ width: 16, height: 16 }}
      />
      <div className="leading-relaxed text-gray-500" style={{ fontSize: 13 }}>
        <RichText content={block.content} />
      </div>
    </div>
  );
}

// Small decorative markers, one per row — a chart-like accent, not a sentiment signal.
const STAT_MARKERS = [
  "bg-blue-500",
  "bg-emerald-500",
  "bg-amber-500",
  "bg-violet-500",
];

function StatRow({ block }: { block: IAdvisorStatRowBlock }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-3.5 py-2.5">
      {block.title && (
        <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
          {block.title}
        </div>
      )}
      <div className="flex flex-col">
        {block.items.map((item, i) => (
          <div key={i} className="flex items-center justify-between gap-4 py-1">
            <div className="flex min-w-0 items-center gap-2.5">
              <span
                className={cn(
                  "h-2.5 w-2.5 shrink-0 rounded-sm",
                  STAT_MARKERS[i % STAT_MARKERS.length]
                )}
              />
              <span className="truncate text-sm text-gray-800">
                {item.label}
              </span>
            </div>
            <span className="shrink-0 text-sm font-semibold text-gray-800">
              {item.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PackageCard({ block }: { block: IAdvisorPackageCardBlock }) {
  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      {block.items.map((item, i) => (
        <div
          key={i}
          className={cn(
            "flex items-center justify-between gap-4 px-4 py-2.5",
            i > 0 && "border-t border-gray-200",
            // The backend flags subtotals and totals; they're the lines that carry the answer.
            item.emphasis && "bg-gray-100"
          )}
        >
          <span
            className={cn(
              "text-sm",
              item.emphasis ? "font-semibold text-gray-800" : "text-gray-500"
            )}
          >
            {item.label}
          </span>
          <span
            className={cn(
              "text-sm",
              item.emphasis ? "font-semibold text-gray-800" : "text-gray-800"
            )}
          >
            {item.value}
          </span>
        </div>
      ))}
    </div>
  );
}

function ComparisonTable({ block }: { block: IAdvisorComparisonTableBlock }) {
  const numeric = numericColumns(block);

  return (
    // Wide tables scroll inside their own container so the page never scrolls sideways.
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-100">
            {block.columns.map((column, i) => (
              <th
                key={i}
                className={cn(
                  "whitespace-nowrap px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-gray-500",
                  numeric[i] ? "text-right" : "text-left"
                )}
              >
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {block.rows.map((row, i) => (
            <tr
              key={i}
              className={cn(
                "border-b border-gray-200 last:border-b-0",
                row.sentiment && ROW_STYLES[row.sentiment]
              )}
            >
              {row.cells.map((cell, j) => (
                <td
                  key={j}
                  className={cn(
                    "whitespace-nowrap px-4 py-2.5 text-gray-800",
                    j === 0 && "font-medium",
                    numeric[j] ? "text-right" : "text-left",
                    // A non-numeric trailing column is supporting prose, not a value.
                    j > 0 && !numeric[j] && "text-gray-500"
                  )}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** S4's bonus breakdown, matched by its distinctive column signature. */
function isBonusBreakdown(block: IAdvisorComparisonTableBlock): boolean {
  return block.columns[0] === "Bonus component";
}

/** S4's "How it compares" table, matched by its distinctive column signature. */
function isS4Comparisons(block: IAdvisorComparisonTableBlock): boolean {
  return block.columns[0] === "Metric" && block.columns[1] === "Proposed";
}

export function BlockRenderer({
  block,
  nodeId,
}: {
  block: AdvisorBlock;
  nodeId?: string;
}) {
  switch (block.type) {
    case "text":
      return <Text block={block} />;
    case "callout":
      return <Callout block={block} />;
    case "stat_row":
      return <StatRow block={block} />;
    case "package_card":
      if (nodeId === "S4_RECOMMEND")
        return <PackageCompositionCard block={block} />;
      return <PackageCard block={block} />;
    case "comparison_table":
      // The two distribution tables get a table/chart toggle, each in its own style; S4's two
      // tables get their own row markers/bullet bars; every other table (S1A outliers, S1B
      // waterfall) stays a plain table.
      if (isChartableDistribution(block)) {
        if (nodeId === "S1B_INTERNAL")
          return <DistributionTable block={block} />;
        if (nodeId === "S1A_MARKET")
          return <MarketPercentileCards block={block} />;
      }
      if (nodeId === "S4_RECOMMEND") {
        if (isBonusBreakdown(block))
          return <BonusBreakdownTable block={block} />;
        if (isS4Comparisons(block)) return <S4ComparisonsTable block={block} />;
      }
      return <ComparisonTable block={block} />;
  }
}

/** The S1B_INTERNAL waterfall table, matched by its distinctive column signature. */
function isPeerWaterfall(block: AdvisorBlock): boolean {
  return (
    block.type === "comparison_table" &&
    block.columns.length === 2 &&
    block.columns[0] === "Layer" &&
    block.columns[1] === "Peer median"
  );
}

/**
 * Drops flag-gated blocks before rendering. Currently: the S1B_INTERNAL peer-median waterfall
 * (and the intro line immediately before it) unless SHOW_PEER_WATERFALL is on.
 */
function visibleBlocks(
  blocks: AdvisorBlock[],
  nodeId?: string
): AdvisorBlock[] {
  if (SHOW_PEER_WATERFALL || nodeId !== "S1B_INTERNAL") return blocks;
  const shown: AdvisorBlock[] = [];
  for (const block of blocks) {
    if (isPeerWaterfall(block)) {
      // Also remove its lead-in line ("Here's how the peer median builds up…"), which the node
      // always emits right before the table.
      if (shown.length > 0 && shown[shown.length - 1].type === "text")
        shown.pop();
      continue;
    }
    shown.push(block);
  }
  return shown;
}

export function BlockList({
  blocks,
  nodeId,
}: {
  blocks: AdvisorBlock[];
  nodeId?: string;
}) {
  return (
    <div className="flex flex-col gap-4">
      {visibleBlocks(blocks, nodeId).map((block, i) => (
        <BlockRenderer key={i} block={block} nodeId={nodeId} />
      ))}
    </div>
  );
}
